<template>
	<div>
		<el-dialog v-dialogDrag top="5vh" custom-class="rtc-private-video-dialog" :title="title" :width="width"
			:visible.sync="showRoom" :close-on-click-modal="false" :close-on-press-escape="false"
			:before-close="onQuit">
			<div class="rtc-private-video">
				<div v-show="isVideo" class="rtc-video-box">
					<div class="rtc-video-friend" v-loading="isWaitingMedia" element-loading-text="连接中..."
						element-loading-background="rgba(0, 0, 0, 0.1)">
						<head-image v-show="!hasRemoteVideo" class="friend-head-image" :id="friend.id" :size="80"
							:name="friend.showNickName" :url="friend.headImage" :isShowUserInfo="false" radius="0">
						</head-image>
						<video ref="remoteVideo" v-show="hasRemoteVideo" autoplay playsinline></video>
					</div>
					<div class="rtc-video-mine" v-show="cameraEnabled">
						<video ref="localVideo" autoplay muted playsinline></video>
					</div>
				</div>
				<div v-show="!isVideo" class="rtc-voice-box" v-loading="isWaitingMedia" element-loading-text="连接中..."
					element-loading-background="rgba(0, 0, 0, 0.1)">
					<head-image class="friend-head-image" :id="friend.id" :size="200" :name="friend.showNickName"
						:url="friend.headImage" :isShowUserInfo="false">
						<div class="rtc-voice-name">{{ friend.showNickName }}</div>
					</head-image>
				</div>
				<div class="rtc-status-line" v-if="statusTip">{{ statusTip }}</div>
				<div class="rtc-control-bar">
					<div class="rtc-control" :class="{ off: !micEnabled }" :title="micEnabled ? '关闭麦克风' : '打开麦克风'"
						@click="toggleMic">
						<i :class="micEnabled ? 'el-icon-microphone' : 'el-icon-turn-off-microphone'"></i>
					</div>
					<div v-if="isVideo" class="rtc-control" :class="{ off: !cameraEnabled }"
						:title="cameraEnabled ? '关闭摄像头' : '打开摄像头'" @click="toggleCamera">
						<i :class="cameraEnabled ? 'el-icon-video-camera-solid' : 'el-icon-video-camera'"></i>
					</div>
					<div class="rtc-control reject" title="挂断" @click="onQuit">
						<i class="el-icon-phone"></i>
					</div>
				</div>
			</div>
		</el-dialog>
		<!-- 音频轨道容器需常驻 DOM，接通瞬间即可出声 -->
		<div ref="remoteAudio" class="rtc-remote-audio"></div>
		<rtc-private-acceptor v-if="!isHost && isRinging" :friend="friend" :mode="mode"
			@accept="onAccept" @reject="onReject"></rtc-private-acceptor>
	</div>
</template>

<script>
import LivekitPrivateApi from '@/api/livekitPrivateApi';
import * as LivekitRoom from '@/api/livekitRoom';
import { LIVEKIT_ACTION, isCallEvent, parseEventContent } from '@/api/livekitAction';
import notificationManager from '@/utils/notification.js';
import HeadImage from '../common/HeadImage.vue';
import RtcPrivateAcceptor from './RtcPrivateAcceptor.vue';

// 重连超过该时长仍未恢复则结束通话，避免界面长期停在重连状态
const RECONNECT_TIMEOUT_MS = 30000;

export default {
	name: 'rtcPrivateVideo',
	components: {
		HeadImage,
		RtcPrivateAcceptor
	},
	data() {
		return {
			API: new LivekitPrivateApi(), // 信令接口
			audio: new Audio(), // 呼叫铃声
			showRoom: false,
			friend: {},
			isHost: false, // 是否主叫
			// IDLE:关闭 CALLING:主叫等待接听 RINGING:被叫响铃 CONNECTING:媒体连接中 CONNECTED:通话中
			state: 'IDLE',
			mode: 'video', // video:视频 voice:语音
			roomTag: '', // 房间标识
			messageId: null, // 通话标识
			micEnabled: true,
			cameraEnabled: false,
			hasRemoteVideo: false, // 远端画面是否可渲染
			ringTipped: false, // 对方是否已响铃
			reconnecting: false,
			videoTime: 0,
			videoTimer: null,
			reconnectTimer: null,
			accepting: false,
			ended: false
		}
	},
	methods: {
		/**
		 * 打开通话窗口
		 * @param {Object} rtcInfo { mode, isHost, friend }，被叫由通话事件驱动
		 */
		open(rtcInfo) {
			if (!rtcInfo || !rtcInfo.friend) {
				return
			}
			if (this.state !== 'IDLE') {
				return
			}
			this.ended = false
			this.mode = rtcInfo.mode === 'video' ? 'video' : 'voice'
			this.isHost = !!rtcInfo.isHost
			this.friend = rtcInfo.friend
			if (this.isHost) {
				this.showRoom = true
				this.startCall()
			}
		},
		initAudio() {
			let url = require(`@/assets/audio/call.wav`);
			this.audio.src = url;
			this.audio.loop = true;
		},
		playRing() {
			try {
				this.audio.currentTime = 0
				this.audio.play()
			} catch (e) {
				// 浏览器可能阻止自动播放，忽略
			}
		},
		stopRing() {
			try {
				this.audio.pause()
				this.audio.currentTime = 0
			} catch (e) {
				// 忽略
			}
		},
		checkDeviceSupport() {
			if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
				this.$message.error('当前浏览器不支持通话，请使用最新版 Chrome 浏览器')
				return false
			}
			return true
		},
		async startCall() {
			const livekit = this.$store.state.configStore.livekit
			if (!livekit || !livekit.url) {
				this.$message.error('通话服务未配置，请联系管理员')
				this.endCall()
				return
			}
			if (!this.checkDeviceSupport()) {
				this.endCall()
				return
			}
			const type = this.isVideo ? 'video' : 'audio'
			this.state = 'CALLING'
			this.playRing()
			try {
				const room = await this.API.call(this.friend.id, type)
				if (!room || !room.token) {
					throw new Error('未获取到房间令牌')
				}
				this.roomTag = room.tag || ''
				this.messageId = room.messageId || null
				await this.connectRoom(room.token)
			} catch (e) {
				this.$message.error(e.message || '发起通话失败，请稍后重试')
				this.endCall()
			}
		},
		/**
		 * 连接房间并发布本地轨道
		 * 主叫连接成功后保持 CALLING，待收到对方接听事件再进入通话中
		 */
		async connectRoom(token) {
			// 对方可能在房间连接完成前就接听，此时不再回退状态
			if (this.state !== 'CONNECTED') {
				this.state = 'CONNECTING'
			}
			const url = this.$store.state.configStore.livekit.url
			try {
				const result = await LivekitRoom.connect({
					url,
					token,
					mode: this.mode,
					onRemoteTrack: this.onRemoteTrack,
					onRemoteTrackRemoved: this.onRemoteTrackRemoved,
					onParticipantConnected: this.onParticipantConnected,
					onParticipantDisconnected: this.onParticipantDisconnected,
					onDisconnected: this.onRoomDisconnected,
					onReconnecting: this.onReconnecting,
					onReconnected: this.onReconnected
				})
				this.cameraEnabled = !!result.cameraEnabled
				if (this.isVideo && !result.cameraEnabled) {
					this.$message.warning('摄像头不可用，已切换为语音通话')
				}
				if (this.cameraEnabled) {
					this.$nextTick(() => LivekitRoom.attachLocalVideo(this.$refs.localVideo))
				}
				if (!this.isHost) {
					this.state = 'CONNECTED'
					this.startChatTime()
				} else if (this.state === 'CONNECTING') {
					// 主叫媒体已就绪，继续等待对方接听
					this.state = 'CALLING'
				}
			} catch (e) {
				this.$message.error(e.message || '连接通话服务失败')
				if (this.roomTag) {
					this.API.finish(this.friend.id, this.roomTag, this.messageId).catch(() => { })
				}
				this.endCall()
			}
		},
		async onAccept() {
			if (this.accepting || this.state !== 'RINGING') {
				return
			}
			this.accepting = true
			this.stopRing()
			try {
				const token = await this.API.accept(this.friend.id, this.roomTag, this.messageId)
				if (!token) {
					throw new Error('未获取到房间令牌')
				}
				this.showRoom = true
				await this.connectRoom(token)
			} catch (e) {
				this.$message.error(e.message || '接听失败，请重试')
				this.API.finish(this.friend.id, this.roomTag, this.messageId).catch(() => { })
				this.endCall()
			} finally {
				this.accepting = false
			}
		},
		onReject() {
			if (this.state !== 'RINGING') {
				return
			}
			this.API.reject(this.friend.id, this.messageId).catch(() => { })
			this.endCall()
		},
		hangup() {
			if (this.roomTag) {
				this.API.finish(this.friend.id, this.roomTag, this.messageId).catch(() => { })
			}
			this.endCall()
		},
		cancelCall() {
			this.API.cancel(this.friend.id).catch(() => { })
			this.endCall()
		},
		/**
		 * 关闭前的统一出口：按当前状态映射为取消/拒绝/挂断
		 */
		onQuit() {
			if (this.state === 'CONNECTED' || this.state === 'CONNECTING') {
				this.hangup()
			} else if (this.state === 'CALLING') {
				this.cancelCall()
			} else if (this.state === 'RINGING') {
				this.onReject()
			} else {
				this.endCall()
			}
		},
		/**
		 * 通话事件入口，由 Home 的事件接入统一调用
		 */
		handleCallEvent(event) {
			if (!event || !event.action) {
				return
			}
			const action = String(event.action)
			if (!isCallEvent(action)) {
				return
			}
			if (action === LIVEKIT_ACTION.CALL) {
				this.onIncomingCall(event)
				return
			}
			// 本地无通话时不处理残留状态事件
			if (this.state === 'IDLE') {
				return
			}
			this.applyAction(action, event)
		},
		async onIncomingCall(event) {
			const myId = this.mine.id
			if (!myId || (event.receiver && event.receiver != myId)) {
				return
			}
			const senderId = event.sender
			if (!senderId) {
				return
			}
			// 已在通话中：以正忙回应，不打断当前通话
			if (this.state !== 'IDLE') {
				// 同一次来电可能经多条通道重复送达，响铃阶段忽略重复的同一来电
				if (this.state === 'RINGING' && this.friend.id == senderId) {
					return
				}
				this.API.busy(senderId, event.id).catch(() => { })
				return
			}
			const content = parseEventContent(event.content)
			if (!content.roomTag) {
				// 缺少房间标识无法接听，直接拒绝避免对端一直等待
				this.API.reject(senderId, event.id).catch(() => { })
				return
			}
			let friend
			try {
				friend = await this.$http({
					url: `/friend/find/${senderId}`,
					method: 'get'
				})
			} catch (e) {
				friend = { id: senderId, showNickName: '未知用户', headImage: '' }
			}
			this.ended = false
			this.friend = friend
			this.mode = content.type === 'video' ? 'video' : 'voice'
			this.isHost = false
			this.roomTag = content.roomTag
			this.messageId = event.id || null
			this.state = 'RINGING'
			this.ringTipped = false
			this.playRing()
			this.API.ring(senderId).catch(() => { })
			this.showCallNotification(senderId, friend)
		},
		applyAction(action, event) {
			const selfSend = event.sender == this.mine.id
			switch (action) {
				case LIVEKIT_ACTION.ACCEPT:
					if (selfSend) {
						this.$message.success('已在其他设备接听')
						this.endCall()
						return
					}
					this.stopRing()
					if (this.state === 'CALLING' || this.state === 'CONNECTING') {
						this.state = 'CONNECTED'
						this.startChatTime()
					}
					break
				case LIVEKIT_ACTION.REJECT:
					this.$message.error(selfSend ? '已在其他设备拒绝' : '对方拒绝了您的通话请求')
					this.endCall()
					break
				case LIVEKIT_ACTION.BUSY:
					this.$message.error('对方正忙，请稍后再拨')
					this.endCall()
					break
				case LIVEKIT_ACTION.HANGUP:
					if (!selfSend) {
						this.$message.success('对方已挂断')
					}
					this.endCall()
					break
				case LIVEKIT_ACTION.CANCEL:
					this.$message.success('对方取消了呼叫')
					this.endCall()
					break
				case LIVEKIT_ACTION.RING:
					this.ringTipped = true
					break
			}
		},
		onRemoteTrack(track) {
			if (track.kind === 'video') {
				this.$nextTick(() => {
					if (this.$refs.remoteVideo) {
						track.attach(this.$refs.remoteVideo)
						this.hasRemoteVideo = true
					}
				})
			} else {
				// 音频轨道需要一个独立容器承载，语音模式下也要出声
				const element = track.attach()
				if (element && this.$refs.remoteAudio) {
					this.$refs.remoteAudio.appendChild(element)
				}
			}
		},
		onRemoteTrackRemoved(track) {
			if (track.kind === 'video') {
				this.hasRemoteVideo = false
			}
			LivekitRoom.detachTrack(track)
		},
		/**
		 * 对方进入房间即视为接通：媒体层是通话状态的权威来源，
		 * 不依赖 IM 信令事件的到达时机
		 */
		onParticipantConnected() {
			if (this.state === 'CALLING' || this.state === 'CONNECTING') {
				this.stopRing()
				this.state = 'CONNECTED'
				this.startChatTime()
			}
		},
		onParticipantDisconnected() {
			if (this.state === 'CONNECTED') {
				this.$message.success('对方已挂断')
			}
			this.endCall()
		},
		onRoomDisconnected() {
			// 主动结束通话时也会触发，已结束时不再提示
			if (this.state !== 'IDLE') {
				this.endCall()
			}
		},
		onReconnecting() {
			this.reconnecting = true
			this.clearReconnectTimer()
			this.reconnectTimer = setTimeout(() => {
				this.$message.error('网络中断，通话已结束')
				if (this.roomTag) {
					this.API.finish(this.friend.id, this.roomTag, this.messageId).catch(() => { })
				}
				this.endCall()
			}, RECONNECT_TIMEOUT_MS)
		},
		onReconnected() {
			this.reconnecting = false
			this.clearReconnectTimer()
		},
		async toggleMic() {
			try {
				this.micEnabled = await LivekitRoom.setMicrophoneEnabled(!this.micEnabled)
			} catch (e) {
				this.$message.error('麦克风切换失败，请检查设备')
			}
		},
		async toggleCamera() {
			try {
				this.cameraEnabled = await LivekitRoom.setCameraEnabled(!this.cameraEnabled)
				if (this.cameraEnabled) {
					this.$nextTick(() => LivekitRoom.attachLocalVideo(this.$refs.localVideo))
				}
			} catch (e) {
				this.$message.error('摄像头切换失败，请检查设备')
			}
		},
		showCallNotification(senderId, friend) {
			try {
				const callInfo = {
					sendId: senderId,
					sendNickName: friend.showNickName,
					sendHeadImage: friend.headImage
				};
				notificationManager.showCallNotification(callInfo, this.mode);
			} catch (error) {
				console.error('显示通话通知失败:', error);
			}
		},
		startChatTime() {
			this.videoTime = 0;
			this.videoTimer && clearInterval(this.videoTimer);
			this.videoTimer = setInterval(() => {
				this.videoTime++;
			}, 1000)
		},
		stopChatTime() {
			this.videoTimer && clearInterval(this.videoTimer);
			this.videoTimer = null;
			this.videoTime = 0;
		},
		clearReconnectTimer() {
			if (this.reconnectTimer) {
				clearTimeout(this.reconnectTimer);
				this.reconnectTimer = null;
			}
		},
		/**
		 * 结束通话并释放全部资源；重复调用安全
		 */
		endCall() {
			if (this.ended) {
				return
			}
			this.ended = true
			this.stopRing()
			this.stopChatTime()
			this.clearReconnectTimer()
			LivekitRoom.disconnect()
			// 清空音频容器，避免下一通电话重复挂载
			if (this.$refs.remoteAudio) {
				this.$refs.remoteAudio.innerHTML = ''
			}
			this.showRoom = false
			this.state = 'IDLE'
			this.hasRemoteVideo = false
			this.cameraEnabled = false
			this.micEnabled = true
			this.reconnecting = false
			this.ringTipped = false
			this.roomTag = ''
			this.messageId = null
			// 允许下一通电话重新进入
			setTimeout(() => {
				this.ended = false
			}, 0)
		}
	},
	computed: {
		mine() {
			return this.$store.state.userStore.userInfo
		},
		width() {
			return this.isVideo ? '960px' : '360px'
		},
		title() {
			let strTitle = `${this.modeText}通话-${this.friend.showNickName}`;
			if (this.isChating) {
				strTitle += `(${this.currentTime})`;
			} else if (this.state === 'CALLING') {
				strTitle += `(呼叫中)`;
			} else if (this.state === 'RINGING') {
				strTitle += `(来电)`;
			}
			return strTitle;
		},
		currentTime() {
			let min = Math.floor(this.videoTime / 60);
			let sec = this.videoTime % 60;
			let strTime = min < 10 ? "0" : "";
			strTime += min;
			strTime += ":"
			strTime += sec < 10 ? "0" : "";
			strTime += sec;
			return strTime;
		},
		isVideo() {
			return this.mode == "video"
		},
		modeText() {
			return this.isVideo ? "视频" : "语音";
		},
		isChating() {
			return this.state === 'CONNECTED';
		},
		isRinging() {
			return this.state === 'RINGING';
		},
		isWaitingMedia() {
			return this.state === 'CONNECTING';
		},
		statusTip() {
			if (this.reconnecting) {
				return '网络不稳定，正在重连...'
			}
			if (this.state === 'CALLING') {
				return this.ringTipped ? '对方已响铃，等待接听' : '等待对方接听'
			}
			if (this.state === 'CONNECTING') {
				return '连接中...'
			}
			if (this.state === 'RINGING') {
				return '邀请你进行通话'
			}
			return ''
		}
	},
	created() {
		// 页面刷新/关闭时尽力通知对端结束通话
		this._onBeforeUnload = () => {
			if (this.state === 'CONNECTED' || this.state === 'CONNECTING') {
				if (this.roomTag) {
					this.API.finish(this.friend.id, this.roomTag, this.messageId).catch(() => { })
				}
			}
		};
		window.addEventListener('beforeunload', this._onBeforeUnload);
	},
	mounted() {
		this.initAudio();
	},
	beforeDestroy() {
		window.removeEventListener('beforeunload', this._onBeforeUnload);
		this.stopRing();
		this.stopChatTime();
		this.clearReconnectTimer();
		LivekitRoom.disconnect();
	}
}
</script>

<style lang="scss">
.rtc-private-video {
	position: relative;

	.el-loading-text {
		color: white !important;
		font-size: 16px !important;
	}

	.path {
		stroke: white !important;
	}

	.rtc-video-box {
		position: relative;
		background-color: #eeeeee;

		.rtc-video-friend {
			height: 70vh;
			background-color: #23262e;

			.friend-head-image {
				position: absolute;
				left: 50%;
				top: 50%;
				transform: translate(-50%, -50%);
			}

			video {
				width: 100%;
				height: 100%;
				object-fit: cover;
			}
		}

		.rtc-video-mine {
			position: absolute;
			z-index: 99999;
			width: 25vh;
			right: 0;
			bottom: -1px;

			video {
				width: 100%;
				object-fit: cover;
				transform: rotateY(180deg);
			}
		}
	}

	.rtc-voice-box {
		position: relative;
		display: flex;
		justify-content: center;
		align-items: center;
		width: 100%;
		height: 300px;
		background-color: var(--im-color-primary-light-9);

		.rtc-voice-name {
			text-align: center;
			font-size: 20px;
			font-weight: 600;
		}
	}

	.rtc-status-line {
		padding-top: 10px;
		text-align: center;
		color: var(--im-text-color-secondary, #909399);
		font-size: 14px;
	}

	.rtc-control-bar {
		display: flex;
		justify-content: center;
		align-items: center;
		gap: 40px;
		padding: 12px 10px 4px;

		.rtc-control {
			display: flex;
			justify-content: center;
			align-items: center;
			width: 52px;
			height: 52px;
			border-radius: 50%;
			background-color: var(--im-color-primary-light-9, #f2f3f5);
			color: var(--im-text-color, #303133);
			font-size: 24px;
			cursor: pointer;
			transition: 0.2s;

			&:hover {
				background-color: var(--im-color-primary-light-8, #e6e8eb);
			}

			&.off {
				color: #c0c4cc;
			}

			&.reject {
				background-color: var(--im-color-danger, #f56c6c);
				color: #fff;

				.el-icon-phone {
					transform: rotate(135deg);
				}
			}
		}
	}

}

// 音频轨道容器需要参与渲染才能出声，但不能占据版面
.rtc-remote-audio {
	position: absolute;
	width: 1px;
	height: 1px;
	overflow: hidden;
	opacity: 0;
	pointer-events: none;
}
</style>
